import React, { useState } from 'react';
import { getScoreReview } from '../utils/geminiReview';

const STAR_ICONS = { 3: '★★★', 2: '★★☆', 1: '★☆☆' };

const BADGE_ICONS = {
  'Speed Star':  '⚡',
  'No Mistakes': '✓',
  'Improving!':  '📈',
};

function barClass(stars) {
  if (stars === 3) return 'fast';
  if (stars === 2) return 'medium';
  return 'slow';
}

function barWidth(avgTimeSec) {
  const pct = Math.max(0, Math.min(100, (1 - avgTimeSec / 8) * 100));
  return `${pct.toFixed(1)}%`;
}

function gradeMessage(grade) {
  const messages = { 'A+': 'Perfect!', A: 'Excellent!', B: 'Good job!', C: 'Keep practicing!', D: 'Try again!' };
  return messages[grade] || '';
}

function TangoResultScreen({ grade, totalTimeSec, wrongTaps, badges = [], categoryStats = [], prevScores, onPlayAgain, onFinish }) {
  const [aiState, setAiState] = useState('idle'); // idle | loading | done | error
  const [aiComment, setAiComment] = useState('');

  const hasPrev = prevScores && categoryStats.some(c => prevScores[c.categoryId]);
  const mins = Math.floor(totalTimeSec / 60).toString().padStart(2, '0');
  const secs = (totalTimeSec % 60).toString().padStart(2, '0');

  const handleReviewScore = async () => {
    setAiState('loading');
    try {
      const comment = await getScoreReview({ grade, categoryStats, wrongTaps, totalTimeSec });
      setAiComment(comment);
      setAiState('done');
    } catch (err) {
      console.error('[geminiReview] failed:', err);
      setAiComment('Great effort! Keep practicing to improve your speed.');
      setAiState('error');
    }
  };

  return (
    <div className="result-overlay">
      <div className="result-modal">

        {/* Header */}
        <div className="result-modal-header">
          <span className="result-modal-title">Results</span>
          <button className="result-close-btn" onClick={onFinish}>&times;</button>
        </div>

        <div className="result-modal-body">

          {/* Grade */}
          <div className="result-grade-area">
            <p className="result-grade">{grade}</p>
            <p className="result-subtitle">
              {gradeMessage(grade)} &nbsp;All done in {mins}:{secs}
              {wrongTaps > 0 && ` · ${wrongTaps} wrong tap${wrongTaps > 1 ? 's' : ''}`}
            </p>
            {badges.length > 0 && (
              <div className="result-badges">
                {badges.map(badge => (
                  <span key={badge} className="result-badge">
                    {BADGE_ICONS[badge]} {badge}
                  </span>
                ))}
              </div>
            )}
          </div>

          <hr className="result-divider" />

          {/* Per-category breakdown */}
          <div>
            <p className="result-section-label">By Category</p>
            {categoryStats.map(cat => (
              <div key={cat.categoryId} className="result-category-row">
                <span className="result-category-name">{cat.name}</span>
                <div className="result-bar-track">
                  <div
                    className={`result-bar-fill ${barClass(cat.stars)}`}
                    style={{ width: barWidth(cat.avgTimeSec) }}
                  />
                </div>
                <span className="result-stars">{STAR_ICONS[cat.stars]}</span>
                <span className="result-time">{cat.avgTimeSec.toFixed(1)}s</span>
              </div>
            ))}
          </div>

          {/* Progress vs last time */}
          {hasPrev && (
            <>
              <hr className="result-divider" />
              <div>
                <p className="result-section-label">vs. Last Time</p>
                {categoryStats.map(cat => {
                  const prev = prevScores[cat.categoryId];
                  if (!prev) return null;
                  const prevSec = (prev.lastAvgMs / 1000).toFixed(1);
                  const currSec = cat.avgTimeSec.toFixed(1);
                  const faster  = cat.avgTimeSec * 1000 < prev.lastAvgMs;
                  return (
                    <div key={cat.categoryId} className="result-progress-row">
                      <span className="result-progress-name">{cat.name}</span>
                      <span className="result-progress-times">{prevSec}s → {currSec}s</span>
                      <span className={`result-progress-delta ${faster ? 'faster' : 'slower'}`}>
                        {faster ? '▲ faster' : '▼ slower'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <hr className="result-divider" />

          {/* Buttons */}
          <div className="result-buttons">
            <button
              className="result-btn-primary"
              onClick={handleReviewScore}
              disabled={aiState === 'loading' || aiState === 'done'}
            >
              ✨ Review Score
            </button>
            <button className="result-btn-secondary" onClick={onPlayAgain}>
              ▶ Play Again
            </button>
            <button className="result-btn-finish" onClick={onFinish}>
              ✕ Finish
            </button>
          </div>

          {/* AI panel */}
          {aiState === 'loading' && <div className="result-spinner" />}
          {(aiState === 'done' || aiState === 'error') && (
            <div className="result-ai-panel">
              <p className="result-ai-label">✨ AI Review</p>
              <p className="result-ai-text">{aiComment}</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default TangoResultScreen;
